import { useI18n } from '../../i18n';
// ============================================
// InventoryPage Component
// Main inventory control page
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import { PageHeader, ActionButton, PageContainer, SearchInput, SelectDropdown, ExportButton } from '../../components/shared';
import {
  useInventoryScope,
  useLowStock,
  useStores,
  useExport,
  useDebounce,
  useStockImports,
  useActiveCurrency,
} from '../../hooks';
import { formatMoney } from '../../utils/currency';
import { useCurrencyScope } from '../../providers/CurrencyProvider';
import {
  readLastRestockStock,
  readMinThreshold,
  resolveStockStatus,
  toStockStatusParam,
} from '../../utils/stockStatus';
import { useStoreSelectionStore } from '../../stores';
import type { InventoryStock, Store } from '@conai/shared-types';
import {
  InventoryStatsGrid,
  InventoryTable,
  InventoryInsightCards,
  AdjustStockModal,
  BulkAdjustModal,
  type InventoryStats,
  type InventoryItem,
} from './components';

type ListEnvelope<T> = {
  results?: T[];
  items?: T[];
  data?: T[] | { results?: T[]; items?: T[]; data?: T[] };
  count?: number;
  total?: number;
  meta?: { count?: number };
};

function toSafeNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

function toSafeString(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function firstImageUrl(value: unknown): string | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  for (const item of value) {
    if (typeof item === 'string' && item.trim().length > 0) {
      return item;
    }
  }
  return undefined;
}

function getAny(source: Record<string, unknown> | null | undefined, keys: string[]): unknown {
  if (!source) return undefined;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  return undefined;
}

function extractList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  const data = payload as ListEnvelope<T> | undefined;
  if (!data) {
    return [];
  }

  if (Array.isArray(data.data)) {
    return data.data;
  }

  if (data.data && typeof data.data === 'object') {
    return data.data.data ?? data.data.results ?? data.data.items ?? [];
  }

  return data.results ?? data.items ?? [];
}

function extractCount<T>(payload: unknown, fallbackCount: number): number {
  if (Array.isArray(payload)) {
    return payload.length;
  }

  const data = payload as ListEnvelope<T> | undefined;
  return data?.count ?? data?.total ?? data?.meta?.count ?? fallbackCount;
}

// Trạng thái tồn kho (IN/LOW/OUT) tính ở utils/stockStatus.ts — DÙNG CHUNG với
// badge trạng thái của từng dòng (InventoryTableRow) và với bộ lọc bên dưới.
// Bug #181: trước đây mapper tự suy diễn tại chỗ, badge lại có công thức riêng
// nên hai bên lệch nhau và lọc "Low Stock"/"Out of Stock" ra bảng rỗng.

// ============================================
// Data Mappers
// ============================================

/**
 * Maps API InventoryStock to UI InventoryItem
 * Field names từ API thực tế (flat object):
 * id, product (uuid), product_name, product_sku,
 * store (uuid), store_name,
 * current_stock, reserved_stock, available_stock, min_stock_threshold,
 * stock_status, updated_at / created_at
 */
function mapInventoryStockToItem(stock: InventoryStock): InventoryItem {
  const raw = stock as unknown as Record<string, unknown>;
  const productRecord = (raw.product && typeof raw.product === 'object' ? raw.product : null) as Record<string, unknown> | null;
  const storeRecord = (raw.store && typeof raw.store === 'object' ? raw.store : null) as Record<string, unknown> | null;
  const inventoryRecord = (raw.inventory && typeof raw.inventory === 'object' ? raw.inventory : null) as Record<string, unknown> | null;

  const productId =
    toSafeString(raw.product_id) ||
    toSafeString(raw.product) ||
    toSafeString(productRecord?.id) ||
    toSafeString(raw.id);

  const productName =
    toSafeString(raw.name) ||
    toSafeString(raw.product_name) ||
    toSafeString(productRecord?.name) ||
    `Product ${String(productId || raw.id || '').slice(0, 8)}`;
  const sku = toSafeString(raw.sku) || toSafeString(raw.product_sku) || toSafeString(productRecord?.sku) || 'N/A';
  const storeName = toSafeString(raw.store_name) || toSafeString(storeRecord?.name) || 'Unknown';


  const imageUrl =
    toSafeString(getAny(raw, ['image_url'])) ||
    firstImageUrl(getAny(raw, ['image_urls'])) ||
    toSafeString(productRecord?.image_url) ||
    firstImageUrl(productRecord?.image_urls) ||
    undefined;

  const quantity = toSafeNumber(
    getAny(raw, ['current_stock', 'quantity']) ?? inventoryRecord?.quantity,
    0
  );
  const reservedQuantity = toSafeNumber(
    getAny(raw, ['reserved_stock', 'reserved_quantity']) ?? inventoryRecord?.reserved_quantity,
    0
  );
  const availableQuantity = toSafeNumber(
    getAny(raw, ['available_stock', 'available_quantity']) ?? inventoryRecord?.available_quantity,
    quantity - reservedQuantity
  );
  // Ngưỡng cảnh báo. Tên chính thức là `min_stock_threshold` (chuỗi decimal, vd
  // "10.000"), nhưng mỗi endpoint gọi một kiểu: `min_threshold` ở
  // /inventory/low-stock/, `min_quantity` ở /inventory/ (docs/API_REFERENCE.md).
  //
  // Từ 09/09/2026 con số này KHÔNG còn do người dùng nhập: BE tính sẵn = 20%
  // mốc tồn sau lần nhập gần nhất, và chỉ để HIỂN THỊ — gửi ngược lên
  // PATCH /inventory/{id}/ là 400.
  //
  // Bug #181: bản trước chỉ đọc 2 tên đầu — payload dùng tên khác thì ngưỡng về
  // 0, điều kiện LOW_STOCK không bao giờ đúng và lọc "Low Stock" ra bảng rỗng.
  // `readMinThreshold` dò đủ các tên đã biết, dò tiếp trong object `product` /
  // `inventory` lồng bên trong, cuối cùng mới tự lấy 20% mốc nhập.
  //
  // Tồn kho là Decimal (hàng cân ký) nên không ép integer.
  const minQuantity = readMinThreshold(raw, inventoryRecord, productRecord);

  // Mốc 100% của thanh tỷ lệ. Trước đây là số BỊA (`minQuantity * 5`, hoặc 100
  // khi không đọc được ngưỡng) vì API không có mốc nào thật. `last_restock_stock`
  // chính là mốc đó, nên cột % giờ mới có nghĩa: 100% = vừa nhập xong, 20% là
  // ranh giới sắp hết. Hàng chưa từng nhập (null) mới rơi lại về ước lượng cũ.
  // `Math.max(..., quantity)` để tồn vượt mốc (vừa nhập thêm ngoài luồng) không
  // vẽ ra thanh quá 100%.
  const lastRestockStock = readLastRestockStock(raw, inventoryRecord, productRecord);
  const maxQuantity = lastRestockStock > 0
    ? Math.max(lastRestockStock, quantity)
    : Math.max(quantity, 100);

  // `stock_status` là tên chính thức. `status` chỉ là dự phòng cho endpoint
  // /inventory/ — và vì resolveStockStatus chỉ nhận đúng các biến thể của 3 giá
  // trị tồn kho, một `status` của SẢN PHẨM ("ACTIVE"/"DRAFT") lọt vào đây cũng
  // bị bỏ qua và rơi xuống nhánh suy diễn, không xếp nhầm trạng thái tồn kho.
  const status = resolveStockStatus(
    getAny(raw, ['stock_status', 'status']) ?? inventoryRecord?.status,
    quantity,
    minQuantity
  );

  const updatedAt = toSafeString(raw.updated_at ?? raw.created_at) || '';

  const locationPercent = maxQuantity > 0 ? Math.round((quantity / maxQuantity) * 100) : 0;
  const locationTrend: 'up' | 'down' | 'stable' =
    status === 'LOW_STOCK' || status === 'OUT_OF_STOCK' ? 'down'
      : locationPercent > 70 ? 'up'
        : 'stable';

  // 1. Base Price (msrpPrice): Giá nhập (cost/purchase_price)
  const costRaw = getAny(raw, ['cost', 'cost_price', 'purchase_price']) ?? productRecord?.cost ?? productRecord?.cost_price;
  const costValue = toSafeNumber(costRaw, NaN);
  const msrpPrice = Number.isFinite(costValue) ? costValue : null;

  // 2. Location Price / selling price (salePrice): Giá bán tại chi nhánh.
  // Thêm 'base_price' theo tiền lệ POS app (usePosCatalog) — sellable-products
  // trả giá bán ở key này, thiếu nó thì salePrice=null -> Margin/Profit về 0/"—".
  const salePriceRaw =
    getAny(raw, ['price', 'store_price', 'selling_price', 'base_price']) ??
    productRecord?.price ??
    productRecord?.selling_price ??
    productRecord?.base_price;
  const salePriceValue = toSafeNumber(salePriceRaw, NaN);
  const salePrice = Number.isFinite(salePriceValue) ? salePriceValue : null;

  // 3. Cost (cost): Giá bán (selling price) để phục vụ render hiển thị ở cột COST hiện tại
  const cost = salePrice;

  // Calculate dynamic margin and profit: Profit = Giá bán (salePrice) - Giá nhập (msrpPrice)
  let margin: number | null = null;
  let profit = 0;
  if (salePrice != null && msrpPrice != null && salePrice > 0) {
    profit = salePrice - msrpPrice;
    margin = (profit / salePrice) * 100;
  }

  return {
    id: String(raw.id ?? ''),
    productId: productId || '',
    productName,
    sku,
    imageUrl,
    address: storeName,
    shelfLocation: storeName,
    currentQuantity: quantity,
    preOrderedQuantity: reservedQuantity,
    availableQuantity,
    minQuantity,
    maxQuantity,
    salePrice,
    msrpPrice,
    cost,
    margin,
    status,
    locationPercent,
    locationTrend,
    profit,
    lastCountedAt: toSafeString(raw.last_counted_at) || null,
    updatedAt,
  };
}

// ============================================
// Component
// ============================================

export function InventoryPage() {
  const { t } = useI18n();
  const navigate = useNavigate();

  // Store selection
  const {
    selectedStoreId,
    availableStores,
    setAvailableStores,
    setSelectedStore,
  } = useStoreSelectionStore();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showStorePicker, setShowStorePicker] = useState(false);
  const [selectedDetailItem, setSelectedDetailItem] = useState<InventoryItem | null>(null);
  /** Dòng đang mở modal điều chỉnh tồn (mục #23/#29/#31 sheet 2). */
  const [adjustItem, setAdjustItem] = useState<InventoryItem | null>(null);
  /**
   * Dòng đã tick để điều chỉnh hàng loạt (mục #23, phần bulk).
   *
   * Giữ nguyên `InventoryItem` chứ không chỉ id: modal cần tên, SKU và tồn
   * hiện tại để hiện "trước → sau", mà dòng đã chọn có thể nằm ở trang khác
   * sau khi người dùng lật trang.
   */
  const [selectedForBulk, setSelectedForBulk] = useState<InventoryItem[]>([]);
  const [bulkModalItems, setBulkModalItems] = useState<InventoryItem[] | null>(null);

  const selectedBulkIds = useMemo(
    () => new Set(selectedForBulk.map((x) => x.id)),
    [selectedForBulk]
  );

  const toggleBulkSelect = useCallback((item: InventoryItem) => {
    setSelectedForBulk((prev) =>
      prev.some((x) => x.id === item.id) ? prev.filter((x) => x.id !== item.id) : [...prev, item]
    );
  }, []);

  // Trang này BẮT BUỘC chọn cửa hàng trước (xem store picker bên dưới) nên
  // luôn có đúng một tiền tệ — dùng của cửa hàng đang chọn (mục #75).
  // `useCurrencyScope` để các component con (bảng, thẻ KPI) tự lấy qua
  // `useMoney()` mà không phải truyền prop xuống từng cái.
  const activeCurrency = useActiveCurrency();
  useCurrencyScope(activeCurrency);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (selectedDetailItem) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedDetailItem]);

  const debouncedSearch = useDebounce(searchQuery, 500);
  const handleSearchChange = (val: string) => { setSearchQuery(val); setCurrentPage(1); };
  const handleStatusChange = (val: string) => { setStatusFilter(val); setCurrentPage(1); };
  const handleLocationChange = (val: string) => {
    // User đã tự đụng filter — khoá đồng bộ từ store tổng trên header.
    locationTouchedRef.current = true;
    setLocationFilter(val);
    setCurrentPage(1);
  };
  const [hasConfirmedStore, setHasConfirmedStore] = useState(!!selectedStoreId);
  const [isStoreSwitching, setIsStoreSwitching] = useState(false);
  const previousStoreIdRef = useRef<string | null>(selectedStoreId);
  const pageSize = 20;
  // Giá trị dropdown -> GIÁ TRỊ GỬI LÊN SERVER, qua đúng một cửa duy nhất.
  //
  // `toStockStatusParam` chỉ trả về phần tử của whitelist hoặc `null`, nên dù ai
  // đó đổi `value` của option ('low_stock', 'Low Stock'...) hay lỡ nhét nhãn đã
  // dịch vào state, thứ đi lên server vẫn luôn là 'IN_STOCK'/'LOW_STOCK'/
  // 'OUT_OF_STOCK' — hoặc không có param nào cả. Không bao giờ là giá trị lạ,
  // mà giá trị lạ giờ bị BE trả 400 chứ không im lặng trả rỗng.
  const stockStatusParam = toStockStatusParam(statusFilter);

  // Cùng pattern trang Orders/Payments: filter Location KHỞI TẠO theo store tổng
  // trên header (lần đầu), sau đó toàn quyền — chọn "All Locations" là xem mọi
  // store, chọn store khác là chuyển scope server-side (không lọc client theo
  // address như bản cũ: dropdown emit store ID, so với address không bao giờ khớp).
  const locationTouchedRef = useRef(false);
  useEffect(() => {
    if (!locationTouchedRef.current && selectedStoreId) {
      setLocationFilter(selectedStoreId);
    }
  }, [selectedStoreId]);

  const isAllLocations = locationFilter === 'all';

  const effectiveStore = isAllLocations ? undefined : (locationFilter || selectedStoreId || undefined);

  // Load active stores for mandatory selection popup and selector
  const { data: storesData, isLoading: isLoadingStores } = useStores({
    page: 1,
    page_size: 100,
    business_status: 'ACTIVE',
  });

  const storesList: Store[] = useMemo(() => extractList<Store>(storesData), [storesData]);

  useEffect(() => {
    if (storesList.length > 0) {
      setAvailableStores(storesList);
    }
  }, [storesList, setAvailableStores]);

  useEffect(() => {
    if (selectedStoreId) {
      setHasConfirmedStore(true);
      setShowStorePicker(false);
    } else {
      setHasConfirmedStore(false);
      setShowStorePicker(true);
    }
  }, [selectedStoreId]);

  // Phạm vi cửa hàng của trang. "All Locations" = LIỆT KÊ TƯỜNG MINH mọi cửa
  // hàng rồi hỏi từng cái, để tổng luôn đúng bằng tổng các cửa hàng (bug #157).
  const scopeStoreIds = useMemo(
    () => (isAllLocations ? availableStores.map((store) => store.id) : effectiveStore ? [effectiveStore] : []),
    [isAllLocations, availableStores, effectiveStore]
  );

  const queriesReady = hasConfirmedStore && !!selectedStoreId;

  // Lọc trạng thái chạy ở SERVER (BE đã chốt hợp đồng: param `stock_status`,
  // 3 giá trị chuẩn, filter bằng SQL đúng công thức của utils/stockStatus.ts).
  //
  // Trước đây bản chữa cháy cho bug #181 bỏ hẳn param và lọc ở client, vì tên và
  // tập giá trị của param chưa được xác nhận. Hệ quả là cửa hàng nào có nhiều
  // dòng hơn một lần tải thì phần dư không bao giờ được lọc. Giờ server lọc trên
  // TOÀN BỘ dữ liệu nên giới hạn đó biến mất.
  //
  // Chỉ đính param khi nhận diện được trạng thái: "All Status" (`null`) thì bỏ
  // hẳn key — không gửi chuỗi rỗng, cũng không gửi cả 3 giá trị.
  const scopeFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      ...(stockStatusParam ? { stock_status: stockStatusParam } : {}),
    }),
    [debouncedSearch, stockStatusParam]
  );

  const {
    rows: inventoryRows,
    isLoading: isLoadingInventory,
    isFetching: isFetchingInventory,
    error: inventoryError,
  } = useInventoryScope(scopeStoreIds, scopeFilters, { enabled: queriesReady });

  // Danh sách RIÊNG cho các thẻ stat: cùng phạm vi cửa hàng nhưng KHÔNG dính
  // search/status — lọc "Out of Stock" thì bảng đổi, Total Stock Value giữ nguyên.
  const { rows: statsRows } = useInventoryScope(scopeStoreIds, {}, { enabled: queriesReady });

  const {
    data: lowStockData,
    isLoading: isLoadingLowStock,
    isFetching: isFetchingLowStock,
  } = useLowStock(
    effectiveStore,
    { page_size: 1 },
    { enabled: hasConfirmedStore && !!selectedStoreId }
  );

  // In Transit (bug #132): đếm phiếu nhập hàng đang trên đường về —
  // SUBMITTED/APPROVED/RECEIVING (loại DRAFT, REJECTED, COMPLETED).
  const { data: stockImportsData } = useStockImports({
    ...(effectiveStore ? { store: effectiveStore } : {}),
    page: 1,
    page_size: 200,
  } as any);

  const inTransitCount = useMemo(() => {
    const imports = extractList<Record<string, unknown>>(stockImportsData);
    const IN_TRANSIT = new Set(['SUBMITTED', 'APPROVED', 'RECEIVING']);
    return imports.filter((po) => IN_TRANSIT.has(String(po.status ?? '').toUpperCase())).length;
  }, [stockImportsData]);

  // Determine if we're loading
  const isLoading = isLoadingInventory || isLoadingLowStock;

  useEffect(() => {
    if (previousStoreIdRef.current !== selectedStoreId && previousStoreIdRef.current !== null) {
      setIsStoreSwitching(true);
      setCurrentPage(1);
      // Dòng đã tick thuộc cửa hàng cũ. Giữ lại thì lô điều chỉnh sẽ gửi
      // product_id của cửa hàng này kèm store_id của cửa hàng kia.
      setSelectedForBulk([]);
    }
    previousStoreIdRef.current = selectedStoreId;
  }, [selectedStoreId]);

  useEffect(() => {
    if (isStoreSwitching && !isFetchingInventory && !isFetchingLowStock) {
      setIsStoreSwitching(false);
    }
  }, [isStoreSwitching, isFetchingInventory, isFetchingLowStock]);

  // Map inventory items
  const inventoryItems = useMemo(
    () => inventoryRows.map((stock) => mapInventoryStockToItem(stock)),
    [inventoryRows]
  );

  // Danh sách cho stats — từ query không filter (chỉ theo phạm vi cửa hàng).
  const statsItems = useMemo(
    () => statsRows.map((stock) => mapInventoryStockToItem(stock)),
    [statsRows]
  );

  const warehouseOptions = useMemo(
    () => availableStores.map((store) => ({ value: store.id, label: `${store.code} - ${store.name}` })),
    [availableStores]
  );

  // KHÔNG lọc lại trạng thái ở đây: `inventoryRows` đã được server lọc theo
  // `stock_status`. Lọc thêm một lần nữa ở client là bắt hai công thức (SQL của
  // BE và derive của FE) phải trùng nhau mãi mãi; lệch một trường hợp biên —
  // ngưỡng null, tồn kho decimal, dòng BE trả `stock_status` khác FE tự suy —
  // là bảng nuốt mất dòng mà không ai truy ra nguyên nhân.
  //
  // Badge từng dòng vẫn tự derive qua utils/stockStatus.ts (mapper ở trên), nên
  // client và server dùng chung ĐÚNG MỘT công thức, chỉ khác chỗ chạy.
  //
  // Phân trang vẫn ở client vì tập hiển thị là hợp của N cửa hàng (fan-out của
  // useInventoryScope, bug #157) — server không biết tổng của cả phạm vi.

  // Calculate stats — từ statsItems (query không filter), KHÔNG cập nhật theo
  // filter/search của bảng: lọc bảng kiểu gì thì các thẻ vẫn là số toàn kho.
  const stats: InventoryStats = useMemo(() => {
    const totalOnHandUnits = statsItems.reduce((sum, item) => sum + toSafeNumber(item.currentQuantity), 0);
    const availableUnits = statsItems.reduce((sum, item) => sum + toSafeNumber(item.availableQuantity), 0);
    const reservedUnits = statsItems.reduce((sum, item) => sum + toSafeNumber(item.preOrderedQuantity), 0);

    const totalStockValue = statsItems.reduce((sum, item) => {
      const unitPrice = item.salePrice ?? item.msrpPrice;
      const safePrice = toSafeNumber(unitPrice, 0);
      return sum + safePrice * toSafeNumber(item.currentQuantity);
    }, 0);
    const derivedLowStockCount = statsItems.filter((item) => item.status === 'LOW_STOCK').length;
    const outOfStockCount = statsItems.filter((item) => item.status === 'OUT_OF_STOCK').length;

    // #97: KHÔNG fallback số mock (12324/1324/24) khi chưa có dữ liệu —
    // kho trống thì hiện 0 thật, không bịa số.
    return {
      totalOnHandUnits,
      availableUnits,
      totalStockValue,
      reservedUnits,
      preOrderedUnits: reservedUnits,
      inTransitCount,
      lowStockAlertCount: Math.max(extractCount(lowStockData, 0), derivedLowStockCount),
      outOfStockCount,
    };
  }, [statsItems, lowStockData, inTransitCount]);

  // Gợi ý nhập hàng cũng tính trên toàn kho, không theo filter bảng.
  const shortageCandidates = useMemo(() => {
    return statsItems
      .map((item) => {
        const available = toSafeNumber(item.availableQuantity, 0);
        const min = toSafeNumber(item.minQuantity, 0);
        const max = toSafeNumber(item.maxQuantity, 0);
        const shortage = Math.max(min - available, 0);
        const suggestedQty = max > 0
          ? Math.max(max - available, shortage, 1)
          : Math.max(shortage, 1);
        return {
          productId: item.productId,
          name: item.productName || 'Unknown Product',
          shortage,
          available,
          min,
          suggestedQty,
        };
      })
      .filter((item) => item.shortage > 0)
      .sort((a, b) => b.shortage - a.shortage)
      .slice(0, 5);
  }, [statsItems]);

  const shortageItems = useMemo(
    () => shortageCandidates.map(({ name, shortage, available, min }) => ({ name, shortage, available, min })),
    [shortageCandidates]
  );

  const shortageProductIds = useMemo(() => {
    return shortageCandidates.slice(0, 10).map((item) => item.productId);
  }, [shortageCandidates]);

  const suggestedQtyParam = useMemo(
    () => shortageCandidates.slice(0, 10).map((item) => `${item.productId}:${item.suggestedQty}`).join(','),
    [shortageCandidates]
  );

  /**
   * HẾT HÀNG lên đầu, rồi SẮP HẾT, rồi còn hàng.
   *
   * Đây là danh sách để HÀNH ĐỘNG, không phải để tra cứu: dòng cần xử lý gấp
   * nhất mà nằm ở trang 4 thì coi như không tồn tại. Trước đây bảng giữ nguyên
   * thứ tự server trả về, nên một cửa hàng vài trăm mặt hàng có thể hết sạch
   * vài món mà không ai thấy cho tới lúc khách hỏi.
   *
   * Sắp xếp NGAY TRƯỚC khi cắt trang, để thứ tự đúng trên toàn tập chứ không
   * chỉ trong phạm vi trang đang xem.
   *
   * Trong cùng một nhóm thì giữ nguyên thứ tự server (`sort` của JS ổn định
   * theo chuẩn) — không tự bịa thêm tiêu chí phụ.
   */
  const sortedItems = useMemo(() => {
    const rank: Record<string, number> = { OUT_OF_STOCK: 0, LOW_STOCK: 1, IN_STOCK: 2 };
    return [...inventoryItems].sort(
      (a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3)
    );
  }, [inventoryItems]);

  // Phân trang trên tập ĐÃ GỘP (và đã được server lọc): tổng ở footer luôn bằng
  // số dòng thực sự duyệt được, không còn cảnh "20 of 137" mà bảng chỉ có 3 dòng
  // (bug #155).
  const totalItems = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const pagedItems = useMemo(
    () => sortedItems.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedItems, currentPage, pageSize]
  );

  // Đổi filter làm số trang co lại — nếu đang đứng ở trang đã biến mất thì kéo
  // về trang cuối còn tồn tại, tránh bảng trống dù có dữ liệu.
  //
  // Chỉ chỉnh khi ĐÃ tải xong: lúc đang fetch, `totalItems` có thể tạm về 0 và
  // effect sẽ đá người dùng ngược về trang 1 ngay khi họ vừa bấm sang trang 2.
  useEffect(() => {
    if (isLoadingInventory || isFetchingInventory) return;
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages, isLoadingInventory, isFetchingInventory]);

  // Handlers
  const handleExport = (format: import('../../hooks/useExport').ExportFormat) => {
    triggerExport(format);
  };

  const { triggerExport, isExporting } = useExport({
    endpoint: '/inventory/',
    params: selectedStoreId ? { store: selectedStoreId } : {},
    filePrefix: 'inventory',
  });

  // Chuyển kho đi qua PHIẾU NHẬP (mode=transfer), KHÔNG qua /inventory/adjust/.
  // Nói rõ vì enum loại điều chỉnh của BE không có TRANSFER_IN/TRANSFER_OUT —
  // ai thấy nút này rồi đi tìm hai loại đó trong modal điều chỉnh sẽ không
  // thấy, và đó là đúng: chuyển kho là hai bút toán ở hai cửa hàng, phải có
  // chứng từ riêng chứ không phải một lần chỉnh tồn.
  const handleTransferStock = () => {
    const params = new URLSearchParams();
    params.set('mode', 'transfer');
    if (selectedStoreId) {
      params.set('storeId', selectedStoreId);
    }
    navigate(`${ADMIN_ROUTES.STOCK_IMPORTS}?${params.toString()}`);
  };

  const handleRowClick = (item: InventoryItem) => {
    setSelectedDetailItem(item);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleReviewReplenishmentQueue = () => {
    const params = new URLSearchParams();
    params.set('mode', 'replenishment');
    if (selectedStoreId) {
      params.set('storeId', selectedStoreId);
    }
    if (shortageProductIds.length > 0) {
      params.set('productIds', shortageProductIds.join(','));
    }
    if (suggestedQtyParam) {
      params.set('suggestedQtys', suggestedQtyParam);
    }
    navigate(`${ADMIN_ROUTES.STOCK_IMPORTS}?${params.toString()}`);
  };

  const handleRowAction = (item: InventoryItem) => {
    const params = new URLSearchParams();
    params.set('mode', 'replenishment');
    if (selectedStoreId) {
      params.set('storeId', selectedStoreId);
    }
    params.set('productIds', item.productId);
    const suggestedQty = Math.max(item.maxQuantity - item.availableQuantity, item.minQuantity - item.availableQuantity, 1);
    params.set('suggestedQtys', `${item.productId}:${suggestedQty}`);
    navigate(`${ADMIN_ROUTES.STOCK_IMPORTS}?${params.toString()}`);
  };

  return (
    <PageContainer>
      {/* Page Header */}
      <PageHeader
        title={t('inventoryControl')}
        subtitle={t('manageWarehouseInventoryAndStockLev')}
        actions={
          <>
            <ExportButton onExport={handleExport} isLoading={isExporting} />
            <ActionButton
              variant="secondary"
              onClick={() => navigate(ADMIN_ROUTES.INVENTORY_ADJUSTMENTS)}
            >
              {t('adjustmentHistoryTitle')}
            </ActionButton>
            {/* Đối soát lệch tồn (#27/#33) — vào từ đây thay vì sidebar, cùng
                cách với lịch sử điều chỉnh: cả hai là màn tra cứu của tồn kho. */}
            <ActionButton
              variant="secondary"
              onClick={() => navigate(ADMIN_ROUTES.INVENTORY_DRIFT)}
            >
              {t('driftTitle')}
            </ActionButton>
            {/* Thống kê hao hụt theo nguyên nhân (#220) — cùng nhóm với hai
                nút tra cứu trên. */}
            <ActionButton
              variant="secondary"
              onClick={() => navigate(ADMIN_ROUTES.INVENTORY_LOSS_STATS)}
            >
              {t('lossStatsTitle')}
            </ActionButton>
            {/* Lô & hạn sử dụng (#32) và Kiểm kê định kỳ (#30) — vào từ đây
                cùng chỗ với hai màn tra cứu trên, không thêm mục sidebar. */}
            <ActionButton
              variant="secondary"
              onClick={() => navigate(ADMIN_ROUTES.INVENTORY_BATCHES)}
            >
              {t('batchesTitle')}
            </ActionButton>
            <ActionButton
              variant="secondary"
              onClick={() => navigate(ADMIN_ROUTES.INVENTORY_STOCKTAKES)}
            >
              {t('stocktakeTitle')}
            </ActionButton>
            <ActionButton
              variant="primary"
              icon={<svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                <path d="M0.163025 4.28041C-0.0522974 4.06508 -0.0522974 3.71084 0.163025 3.49552L3.49705 0.161492C3.71238 -0.0538307 4.06662 -0.0538307 4.28194 0.161492C4.49726 0.376815 4.49726 0.731055 4.28194 0.946378L1.89603 3.33229H15.0029C15.3085 3.33229 15.5586 3.58234 15.5586 3.88796C15.5586 4.19358 15.3085 4.44363 15.0029 4.44363H1.89603L4.28194 6.82955C4.49726 7.04487 4.49726 7.39911 4.28194 7.61443C4.06662 7.82976 3.71238 7.82976 3.49705 7.61443L0.163025 4.28041ZM12.0613 16.5052C11.846 16.7205 11.4918 16.7205 11.2765 16.5052C11.0611 16.2899 11.0611 15.9356 11.2765 15.7203L13.6624 13.3344H0.555468C0.249848 13.3344 -0.000203609 13.0843 -0.000203609 12.7787C-0.000203609 12.4731 0.249848 12.223 0.555468 12.223H13.6624L11.2765 9.83712C11.0611 9.6218 11.0611 9.26756 11.2765 9.05223C11.4918 8.83691 11.846 8.83691 12.0613 9.05223L15.3954 12.3863C15.6107 12.6016 15.6107 12.9558 15.3954 13.1711L12.0613 16.5052Z" fill="white" />
              </svg>}
              onClick={handleTransferStock}
            >
              {t('transferStock')}
            </ActionButton>
          </>
        }
      />

      {/* Error Message */}
      {inventoryError && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadInventoryData')}</p>
          <p className="mt-1 text-red-600">
            {inventoryError instanceof Error ? inventoryError.message : t('inventoryLoadErrorGeneric')}
          </p>
          {/* Lọc trạng thái giờ chạy ở server. Nếu bản BE đang chạy chưa hiểu
              param `stock_status` thì request hỏng (400) và bảng trống — chỉ rõ
              cách gỡ tạm để người dùng không tưởng là kho rỗng. */}
          {stockStatusParam && (
            <button
              type="button"
              onClick={() => handleStatusChange('all')}
              className="mt-2 block font-medium text-red-700 underline"
            >
              {t('inventoryStatusFilterErrorHint')}
            </button>
          )}
        </div>
      )}

      {/* Mandatory Store Picker */}
      {showStorePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-slate-900">{t('inventorySelectStoreTitle')}</h3>
            <p className="mt-2 text-sm text-slate-600">
              {t('inventorySelectStoreDesc')}
            </p>

            {isLoadingStores ? (
              <div className="mt-6 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />
                ))}
              </div>
            ) : (
              <div className="mt-6 max-h-80 space-y-3 overflow-y-auto">
                {availableStores.map((store) => (
                  <button
                    key={store.id}
                    onClick={() => {
                      setSelectedStore(store);
                      setCurrentPage(1);
                      setShowStorePicker(false);
                      setHasConfirmedStore(true);
                      setIsStoreSwitching(true);
                    }}
                    className="w-full rounded-lg border border-slate-200 p-4 text-left hover:border-primary-400 hover:bg-primary-50"
                  >
                    <p className="font-semibold text-slate-900">{store.name}</p>
                    <p className="mt-1 text-sm text-slate-500">{store.code} - {store.address}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="mt-6">
        <InventoryStatsGrid stats={stats} isLoading={isLoading || isStoreSwitching || !selectedStoreId || !hasConfirmedStore} />
      </div>

      {/* Filter Bar */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={t('searchProducts')}
          className="w-full sm:w-[280px]"
          width="auto"
        />
        <div className="grid w-full grid-cols-2 gap-3 sm:flex sm:w-auto sm:items-center">
          <SelectDropdown
            options={[
              { value: 'all', label: t('allLocations') },
              ...warehouseOptions,
            ]}
            value={locationFilter}
            onChange={handleLocationChange}
            placeholder={t('allLocations')}
            className="min-w-0 sm:w-[180px]"
          />
          <SelectDropdown
            options={[
              { value: 'all', label: t('allStatus') },
              { value: 'IN_STOCK', label: t('inStock') },
              { value: 'LOW_STOCK', label: t('lowStock') },
              { value: 'OUT_OF_STOCK', label: t('outOfStock') },
            ]}
            value={statusFilter}
            onChange={handleStatusChange}
            placeholder={t('allStatus')}
            className="min-w-0 sm:w-[160px]"
          />
        </div>
      </div>

      {/* Thanh hành động hàng loạt — chỉ hiện khi đã tick ít nhất một dòng */}
      {selectedForBulk.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
          <span className="text-sm font-semibold text-primary-800">
            {t('bulkAdjustSelectedCount').replace('{count}', String(selectedForBulk.length))}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedForBulk([])}
              className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
            >
              {t('bulkAdjustClearSelection')}
            </button>
            <button
              onClick={() => setBulkModalItems(selectedForBulk)}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
            >
              {t('bulkAdjustOpen')}
            </button>
          </div>
        </div>
      )}

      {/* Inventory Table */}
      <div className="mt-4">
        <InventoryTable
          data={isStoreSwitching ? [] : pagedItems}
          isLoading={isLoading || isStoreSwitching || !selectedStoreId || !hasConfirmedStore}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onRowClick={handleRowClick}
          onRowActionClick={handleRowAction}
          selectedIds={selectedBulkIds}
          onToggleSelect={toggleBulkSelect}
        />
      </div>

      {/* Insight Cards */}
      <div className="mt-8">
        <InventoryInsightCards
          lowStockCount={stats.lowStockAlertCount}
          outOfStockCount={stats.outOfStockCount}
          topShortages={shortageItems}
          onReviewQueue={handleReviewReplenishmentQueue}
        />
      </div>

      {/* Inventory Item Detail Modal */}
      {selectedDetailItem && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={() => setSelectedDetailItem(null)}
        >
          <div 
            className="w-full max-w-[500px] overflow-hidden rounded-2xl bg-white shadow-2xl animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h3 className="text-lg font-bold text-slate-900">{t('inventoryDetails') || 'Inventory Details'}</h3>
              <button 
                onClick={() => setSelectedDetailItem(null)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {selectedDetailItem.imageUrl && (
                <div className="flex justify-center mb-4">
                  <img 
                    src={selectedDetailItem.imageUrl} 
                    alt={selectedDetailItem.productName} 
                    className="h-32 w-32 object-contain rounded-xl border border-slate-100 p-2"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">{t('productNameLabel') || 'Product Name'}</span>
                  <span className="font-semibold text-slate-900">{selectedDetailItem.productName}</span>
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">SKU</span>
                  <span className="font-semibold text-slate-900">{selectedDetailItem.sku}</span>
                </div>

                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">{t('storeColumn') || 'Store'}</span>
                  <span className="text-slate-700">{selectedDetailItem.address}</span>
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">STATUS</span>
                  <span className="inline-block mt-0.5 rounded px-2 py-0.5 text-xs font-bold uppercase bg-slate-100 text-slate-700">
                    {selectedDetailItem.status}
                  </span>
                </div>

                <div className="col-span-2 border-t border-slate-100 pt-3" />

                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">{t('costPrice') || 'Cost Price'}</span>
                  <span className="font-semibold text-slate-900">
                    {selectedDetailItem.cost != null ? formatMoney(selectedDetailItem.cost, { currency: activeCurrency }) : '-'}
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">{t('basePrice')}</span>
                  <span className="font-semibold text-slate-900">
                    {selectedDetailItem.msrpPrice != null ? formatMoney(selectedDetailItem.msrpPrice, { currency: activeCurrency }) : '-'}
                  </span>
                </div>

                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">{t('locationPrice')}</span>
                  <span className="font-semibold text-slate-900">
                    {selectedDetailItem.salePrice != null ? formatMoney(selectedDetailItem.salePrice, { currency: activeCurrency }) : '-'}
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">{t('margin')}</span>
                  <span className="font-semibold text-slate-900">
                    {selectedDetailItem.margin != null ? `${selectedDetailItem.margin.toFixed(2)}%` : '-'}
                  </span>
                </div>

                <div className="col-span-2 border-t border-slate-100 pt-3" />

                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">{t('currentStock')}</span>
                  <span className="font-bold text-slate-900">{selectedDetailItem.currentQuantity} UNITS</span>
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">{t('reservedStock')}</span>
                  <span className="font-bold text-slate-900">{selectedDetailItem.preOrderedQuantity} UNITS</span>
                </div>

                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400">{t('availableStock')}</span>
                  <span className="font-bold text-slate-900 text-blue-600">{selectedDetailItem.availableQuantity} UNITS</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
              <button
                onClick={() => setSelectedDetailItem(null)}
                className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 transition-colors"
              >
                {t('close')}
              </button>
              <button
                onClick={() => setAdjustItem(selectedDetailItem)}
                className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
              >
                {t('adjustStockTitle')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Điều chỉnh tồn kho — POST /inventory/adjust/ (mục #23/#29/#31).
          `effectiveStore` là cửa hàng đang xem; khi chọn "All locations" thì
          rơi về cửa hàng đang chọn ở scope, vì bút toán luôn thuộc MỘT kho. */}
      <AdjustStockModal
        item={adjustItem}
        storeId={effectiveStore || selectedStoreId || undefined}
        onClose={() => setAdjustItem(null)}
        onSuccess={() => setSelectedDetailItem(null)}
      />

      {/* Điều chỉnh HÀNG LOẠT — POST /inventory/bulk-adjust/ (mục #23 phần bulk) */}
      {bulkModalItems && (
        <BulkAdjustModal
          items={bulkModalItems}
          storeId={effectiveStore || selectedStoreId || undefined}
          onClose={() => setBulkModalItems(null)}
          onSuccess={() => setSelectedForBulk([])}
        />
      )}
    </PageContainer>
  );
}
